import { ChevronDown, ChevronUp, LogOut, Menu, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { authApi } from "../api/axios";
import { useAuth } from "../auth/useAuth";
import { useMenu } from "../context/MenuContext";
import { useSession } from "../session/useSession";

const Header = ({
  externalBaseUrl: propBaseUrl,
  internalLinks = [],
  externalBasename = "",
}) => {
  const baseUrl = propBaseUrl || "";
  const baseName = externalBasename.replace(/\/+$/, "");
  const menuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const { user, logout, isClientLogin, token } = useAuth();
  const { mainMenus: menuMain, profileMenus: menuProfile } = useMenu();
  const { performLogout: sessionPerformLogout } = useSession();

  const [profileOpen, setProfileOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [mobileFinanceOpen, setMobileFinanceOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileProfileExpanded, setMobileProfileExpanded] = useState(false);

  const displayName = (() => {
    if (!user) return "Guest";

    return user.fullName?.trim() || "User";
  })();

  const initials = (() => {
    if (!displayName || displayName === "Guest") return "G";
    const cleanName = displayName.replace(/[^a-zA-Z\s]/g, "").trim();
    const parts = cleanName.split(" ").filter(Boolean);
    if (parts.length === 0) return "U";
    return parts[0].charAt(0).toUpperCase();
  })();

  const avatarGradient = useMemo(() => {
    const gradients = [
      "from-blue-700 to-blue-500",
      "from-purple-700 to-pink-500",
      "from-emerald-700 to-teal-500",
      "from-orange-700 to-red-500",
      "from-indigo-700 to-purple-500",
      "from-pink-700 to-rose-500",
      "from-teal-700 to-cyan-500",
      "from-red-700 to-orange-500",
      "from-violet-700 to-fuchsia-500",
      "from-sky-700 to-indigo-500",
    ];
    const seed = user?.fullName || displayName;
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }
    return gradients[Math.abs(hash) % gradients.length];
  }, [user?.fullName, displayName]);

  const clientBalance = user?.clientBalance?.amount || 0;
  const currency = user?.branch?.currency || "INR";

  const showBalance =
    isClientLogin && user?.client?.accountType?.toLowerCase() === "deposit";

  useEffect(() => {
    const handler = (e) => {
      const insideDesktopMenu =
        menuRef.current && menuRef.current.contains(e.target);
      const insideMobileMenu =
        mobileMenuRef.current && mobileMenuRef.current.contains(e.target);

      if (!insideDesktopMenu && !insideMobileMenu) {
        setProfileOpen(false);
        setFinanceOpen(false);
        setMobileFinanceOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, []);
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    closeMenus();
  }, [location.pathname]);

  const handleLogout = useCallback(async () => {
    // Call the backend logout endpoint first so the server records
    // the logout server-side. Whether it succeeds or fails, we then
    // delegate to SessionProvider.performLogout which:
    //   1. Broadcasts `LOGOUT` over the cross-tab BroadcastChannel so
    //      every sibling tab's SessionProvider receives it and logs
    //      out + redirects in the same tick.
    //   2. Clears local auth state via AuthContext.logout() (which
    //      removes cf_auth_session, user, otpUserId, refreshToken).
    //   3. Redirects this tab to the login page.
    //
    // This is the same performLogout the idle-modal countdown, the
    // refresh-fail path, and the 401 interceptor use — single source
    // of truth for "log out everywhere, immediately."
    try {
      await authApi.post("/account/logout");
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      if (sessionPerformLogout) {
        sessionPerformLogout();
      } else {
        // Fallback for the rare case SessionProvider isn't mounted
        // yet (e.g. Header rendered outside the protected tree).
        // Matches the old behavior so the user is never stuck.
        logout();
        localStorage.clear();
        window.location.href = window.location.origin;
      }
    }
  }, [sessionPerformLogout, logout]);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const closeMenus = () => {
    setProfileOpen(false);
    setFinanceOpen(false);
    setMobileFinanceOpen(false);
    setMobileOpen(false);
    setMobileProfileExpanded(false);
  };

  const externalUrl = (path) => {
    if (!baseUrl) return path;
    const prefixed = baseName ? `${baseName}${path}` : path;
    const url = new URL(prefixed, baseUrl);
    if (token) url.searchParams.set("token", token);
    if (user) url.searchParams.set("user", JSON.stringify(user));
    return url.toString();
  };

  const mapMenuItem = useCallback(
    (item) => {
      if (item.menuName === "Logout") {
        return { name: "Logout", action: handleLogout };
      }
      const mapped = {
        name: item.menuName,
        path: item.menuLink,
      };
      if (item.children?.length) {
        mapped.children = item.children.map((c) => ({
          name: c.menuName,
          path: c.menuLink,
        }));
      }
      return mapped;
    },
    [handleLogout],
  );

  const mainMenus = useMemo(
    () => menuMain.map(mapMenuItem),
    [menuMain, mapMenuItem],
  );

  const profileMenus = useMemo(
    () => menuProfile.map(mapMenuItem),
    [menuProfile, mapMenuItem],
  );

  const isMenuActive = useCallback(
    (menu) => {
      if (!menu.path) return false;
      return location.pathname.startsWith(menu.path);
    },
    [location.pathname],
  );

  const closeAndNav = (path) => {
    closeMenus();
    setTimeout(() => navigate(path), 0);
  };

  const renderMainMenus = (isMobile = false) =>
    mainMenus.map((menu, index) => {
      const active = isMenuActive(menu);

      const baseClass = isMobile
        ? `block p-4 rounded-2xl border transition-all duration-200 no-underline ${
            active
              ? "bg-primaryColor/10 border-primaryColor text-primaryColor shadow-sm"
              : "bg-white border-gray-200 text-gray-700 hover:border-primaryColor hover:shadow-md"
          }`
        : `px-4 py-2 text-sm font-bold transition-all rounded-full no-underline ${
            active
              ? "text-primaryColor bg-primaryColor/10"
              : "text-gray-600 hover:bg-gray-50 hover:text-primaryColor"
          }`;

      const isExternalLink =
        menu.external && baseUrl && !internalLinks.includes(menu.name);

      if (isExternalLink) {
        if (isMobile) {
          return (
            <a
              key={index}
              href={externalUrl(menu.path)}
              className={baseClass}
              onClick={closeMenus}
            >
              {menu.name}
            </a>
          );
        }
        return (
          <a
            key={index}
            href={externalUrl(menu.path)}
            className={baseClass}
            onClick={closeMenus}
          >
            {menu.name}
          </a>
        );
      }

      if (isMobile) {
        return (
          <button
            key={index}
            className={`w-full text-left ${baseClass}`}
            onClick={() => closeAndNav(menu.path)}
          >
            {menu.name}
          </button>
        );
      }

      return (
        <NavLink
          key={index}
          to={menu.path}
          className={baseClass}
          onClick={closeMenus}
        >
          {menu.name}
        </NavLink>
      );
    });

  const renderProfileMenus = (isMobile = false) =>
    profileMenus.map((menu, index) => {
      if (menu.name === "Finance") {
        const isFinanceOpen = isMobile ? mobileFinanceOpen : financeOpen;

        const setFinanceState = isMobile
          ? setMobileFinanceOpen
          : setFinanceOpen;

        return (
          <div key={index} className="w-full">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFinanceState((prev) => !prev);
              }}
              className="w-full flex justify-between items-center px-4 py-2.5 rounded-xl font-medium text-sm text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Finance
              <ChevronDown
                size={14}
                className={`transition-transform duration-200 ${
                  isFinanceOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {isFinanceOpen &&
              menu.children?.map((child, childIndex) => {
                const isChildExternal =
                  child.external &&
                  baseUrl &&
                  !internalLinks.includes(child.name);
                return isChildExternal ? (
                  <a
                    key={childIndex}
                    href={externalUrl(child.path)}
                    onClick={closeMenus}
                    className="block pl-10 pr-4 py-2 text-sm font-semibold text-gray-500 hover:text-primaryColor no-underline"
                  >
                    {child.name}
                  </a>
                ) : (
                  <Link
                    key={childIndex}
                    to={child.path}
                    onClick={closeMenus}
                    className="block pl-10 pr-4 py-2 text-sm font-semibold text-gray-500 hover:text-primaryColor no-underline"
                  >
                    {child.name}
                  </Link>
                );
              })}
          </div>
        );
      }

      if (menu.action) {
        return (
          <button
            key={index}
            onClick={() => {
              closeMenus();
              menu.action();
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-bold text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut size={16} />
            {menu.name}
          </button>
        );
      }

      if (menu.path) {
        const isExternalProfile =
          menu.external && baseUrl && !internalLinks.includes(menu.name);
        if (isExternalProfile) {
          return (
            <a
              key={index}
              href={externalUrl(menu.path)}
              onClick={closeMenus}
              className="block px-4 py-2.5 rounded-xl text-sm font-bold text-gray-700 hover:bg-gray-100 transition-colors no-underline"
            >
              {menu.name}
            </a>
          );
        }
        return (
          <Link
            key={index}
            to={menu.path}
            className="block px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors no-underline"
            onClick={closeMenus}
          >
            {menu.name}
          </Link>
        );
      }

      return null;
    });

  return (
    <header className="sticky bg-white top-0 z-[1000] border-b border-gray-200 shadow-md font-sans">
      <nav className="mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* LOGO */}
          <div
            onClick={() => navigate("/search")}
            className="cursor-pointer shrink-0 pl-2 md:pl-4 lg:pl-6"
            style={{ paddingLeft: "1.5rem" }}
          >
            <img
              src={`${import.meta.env.PUBLIC_CLOUDFRONT_BASE_URL}/assets/corefares-logo.svg`}
              alt="CoreFares Logo"
              style={{ height: 48, width: "auto" }}
            />
          </div>

          {/* RIGHT SECTION */}
          <div className="flex items-center gap-2" ref={menuRef}>
            {/* Desktop Menus */}
            {isDesktop && (
              <div className="flex items-center gap-1">
                {renderMainMenus(false)}
              </div>
            )}

            {/* PROFILE */}
            {isDesktop && (
              <div className="relative">
                <div
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-3 pl-1 pr-3 py-1 rounded-full border border-gray-200 bg-white hover:shadow-md transition-all cursor-pointer"
                >
                  {/* AVATAR */}
                  <div className={`w-9 h-9 rounded-full bg-gradient-to-tr ${avatarGradient} flex items-center justify-center text-white font-bold text-sm shrink-0`}>
                    {initials}
                  </div>

                  {/* USER INFO */}
                  <div className="flex flex-col leading-tight">
                    <span className="text-sm font-semibold text-gray-800 max-w-[140px] truncate">
                      {displayName}
                    </span>

                    <span className="text-xs uppercase tracking-wider text-gray-400 font-bold">
                      {isClientLogin ? "Client" : "Staff"}
                    </span>
                  </div>

                  {/* ICON */}
                  <ChevronDown
                    size={16}
                    className={`text-gray-400 transition-transform duration-200 ${
                      profileOpen ? "rotate-180" : ""
                    }`}
                  />
                </div>

                {profileOpen && (
                  <div className="absolute top-full right-0 mt-3 w-72 bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-xl">
                    {/* USER INFO */}
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full bg-gradient-to-tr ${avatarGradient} flex items-center justify-center text-white font-bold text-lg`}>
                          {initials}
                        </div>

                        <div>
                          <p className="text-sm font-bold text-gray-900">
                            {displayName}
                          </p>

                          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                            {isClientLogin ? "Client Account" : "Staff Account"}
                          </p>
                        </div>
                      </div>

                      {/* BALANCE */}
                      {showBalance && (
                        <div className="mt-4 bg-white border border-gray-200 rounded-xl p-3">
                          <p className="text-xs uppercase tracking-widest text-gray-400 font-bold">
                            Available Balance
                          </p>

                          <p className="text-lg font-black text-primaryColor mt-1">
                            {currency} {clientBalance.toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* MENU ITEMS */}
                    <div className="p-2">{renderProfileMenus(false)}</div>
                  </div>
                )}
              </div>
            )}

            {/* MOBILE MENU BUTTON */}
            {!isDesktop && (
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="p-2 rounded-lg bg-gray-50"
              >
                {mobileOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            )}
          </div>
        </div>

        {/* MOBILE MENU */}
        {!isDesktop && mobileOpen && (
          <div
            ref={mobileMenuRef}
            className="fixed inset-0 top-20 z-[999] overflow-hidden"
            onClick={() => setMobileOpen(false)}
          >
            <div className="absolute inset-0 bg-black/40" />
            <div
              className="absolute right-0 top-0 h-full w-full max-w-sm bg-white shadow-2xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 space-y-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-gray-900">Menu</p>
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="p-2 -mr-2 rounded-lg hover:bg-gray-100 transition-colors"
                    aria-label="Close menu"
                  >
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>

                {/* WALLET CARD */}
                {showBalance && (
                  <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-5 rounded-2xl text-white shadow-lg">
                    <p className="text-xs font-bold opacity-80 uppercase tracking-widest">
                      Available Balance
                    </p>
                    <p className="text-2xl font-black mt-1">
                      <span className="text-base font-medium opacity-70">
                        {currency}
                      </span>{" "}
                      {clientBalance.toLocaleString()}
                    </p>
                  </div>
                )}

                {/* MAIN NAVIGATION */}
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-3 px-1">
                    Navigation
                  </p>
                  <div className="grid grid-cols-2 gap-2.5">
                    {renderMainMenus(true)}
                  </div>
                </div>

                {/* PROFILE FOOTER */}
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-3 px-1">
                    Account
                  </p>
                  <div
                    onClick={() =>
                      setMobileProfileExpanded(!mobileProfileExpanded)
                    }
                    className="flex items-center justify-between p-3.5 bg-gray-50 rounded-2xl cursor-pointer hover:bg-gray-100 transition-colors border border-gray-100"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${avatarGradient} flex items-center justify-center text-white font-bold text-sm shadow-sm`}>
                        {initials}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">
                          {displayName}
                        </p>
                      </div>
                    </div>
                    {mobileProfileExpanded ? (
                      <ChevronUp size={18} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={18} className="text-gray-400" />
                    )}
                  </div>

                  {mobileProfileExpanded && (
                    <div className="mt-2 bg-gray-50 rounded-2xl p-1.5 border border-gray-100">
                      {renderProfileMenus(true)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
